/**
 * Template Renderer
 *
 * Replaces {{variable.key}} macro tokens in subject, body, and plain text
 * fallback with resolved values or example values for preview.
 */
import type { NotificationTemplateVariable } from '../notification-template.types';

const MACRO_PATTERN = /\{\{([^}]+)\}\}/g;

/**
 * Render a template string by replacing {{key}} tokens with values.
 * Returns the rendered string and a list of unresolved required variables.
 */
export function renderTemplateString(
  template: string,
  variables: NotificationTemplateVariable[],
  values: Record<string, string>,
): { rendered: string; unresolvedRequired: string[] } {
  const unresolvedRequired: string[] = [];

  const rendered = template.replace(MACRO_PATTERN, (_match, key: string) => {
    const trimmedKey = key.trim();
    const value = values[trimmedKey];
    if (value !== undefined && value !== '') return value;

    const varDef = variables.find((v) => v.key === trimmedKey);
    if (varDef?.required) {
      unresolvedRequired.push(trimmedKey);
    }
    // Show a visible fallback in preview when no example value is provided
    if (varDef) {
      return `[${varDef.label || trimmedKey}]`;
    }
    return `[${trimmedKey}]`;
  });

  return { rendered, unresolvedRequired };
}

/**
 * Build a value map from example values on variable definitions.
 */
export function buildExampleValueMap(
  variables: NotificationTemplateVariable[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const v of variables) {
    if (v.example) {
      map[v.key] = v.example;
    }
  }
  return map;
}

/**
 * Render subject, HTML body, and plain text fallback using example values for preview.
 */
export function renderPreview(
  subject: string,
  htmlBody: string,
  textBody: string,
  variables: NotificationTemplateVariable[],
): { subject: string; htmlBody: string; textBody: string; unresolvedRequired: string[] } {
  const exampleValues = buildExampleValueMap(variables);
  const subjectResult = renderTemplateString(subject, variables, exampleValues);
  const bodyResult = renderTemplateString(htmlBody, variables, exampleValues);
  const textResult = renderTemplateString(textBody, variables, exampleValues);
  return {
    subject: subjectResult.rendered,
    htmlBody: bodyResult.rendered,
    textBody: textResult.rendered,
    unresolvedRequired: [
      ...new Set([
        ...subjectResult.unresolvedRequired,
        ...bodyResult.unresolvedRequired,
        ...textResult.unresolvedRequired,
      ]),
    ],
  };
}

/**
 * Extract all variable keys used in a template string.
 */
export function extractVariableKeys(template: string): string[] {
  MACRO_PATTERN.lastIndex = 0;
  const keys: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = MACRO_PATTERN.exec(template)) !== null) {
    keys.push(match[1].trim());
  }
  return [...new Set(keys)];
}

/**
 * All distinct variable keys referenced in subject/body/text and declared on the template.
 */
export function collectVariableKeysFromTemplate(config: {
  subject: string;
  htmlBody: string;
  textBody?: string;
  variables: NotificationTemplateVariable[];
}): string[] {
  const keys = new Set<string>();
  for (const v of config.variables) {
    if (v.key?.trim()) keys.add(v.key.trim());
  }
  for (const k of extractVariableKeys(config.subject)) keys.add(k);
  for (const k of extractVariableKeys(config.htmlBody)) keys.add(k);
  for (const k of extractVariableKeys(config.textBody ?? '')) keys.add(k);
  return [...keys];
}
